const parsePhoneNumber = require('./parse-phone-number')
const countryCodesByLocale = require('../constants/country-codes-by-locale')
const countryCodes = require('../constants/country-codes')
const PNF = require('google-libphonenumber').PhoneNumberFormat
const phoneUtil = require('google-libphonenumber').PhoneNumberUtil.getInstance()
const AsYouTypeFormatter = require('google-libphonenumber').AsYouTypeFormatter

const extensionSuffixByCountryCode = {
  1: 'ext.',
  33: 'poste',
}

/**
 * Formats phone numbers in a locale-aware way.
 * @param {string} str A parsable phone number string (eg +1 1234567890, 1234567890, 1234567890 123)
 * @param {string} [opts] Optional string
 * @param {object} config Configuration object
 * @param {string} [config.locale=en-US] A locale in hyphen format (eg en-US)
 * @returns {string} The formatted number
 */
function phone(str, opts, config) {
  let formattedNumber

  try {
    const defaultCode =
      countryCodesByLocale[(config && config.locale) || 'en-US']
    let data =
      typeof parsePhoneNumber(str) !== 'boolean' ? parsePhoneNumber(str) : {}

    // add the country code based on current locale if not provided in number
    data.country_code = data.country_code || defaultCode
    let extensionSuffix = extensionSuffixByCountryCode[data.country_code]
    let showInternational =
      opts === 'INCLUDE_COUNTRY_CODE' ? PNF.INTERNATIONAL : PNF.NATIONAL

    if (data.extension) {
      // strip extension
      let splitNumber = str.split(' ')
      let numberWithoutExtension = splitNumber.slice(0, splitNumber.length - 1)

      // parse number with google
      formattedNumber = phoneUtil.format(
        phoneUtil.parseAndKeepRawInput(numberWithoutExtension.join(' '), 'US'),
        showInternational
      )

      // add the extension at the end
      let prefix = extensionSuffix ? extensionSuffix + ' ' : 'x'
      return formattedNumber + ' ' + prefix + data.extension
    } else {
      // just parse the number with google
      formattedNumber = phoneUtil.format(
        phoneUtil.parseAndKeepRawInput(str, 'US'),
        showInternational
      )
    }
  } catch (error) {
    console.error(error)
    console.error('i18nUtils.phone could not parse the input: ' + str)
    return str
  }

  return formattedNumber
}

const utils = {
  /**
   * This function takes a string that represents user input of a phone number and then manually inputs that string,
   * one character at a time into the formatter. The result is a well formatted object representing a phone number,
   * it's format in RegEx and boolean value represents that it is valid for given country code or not.
   *
   * For example given the examples in the parameters below the returned object would be:
   *
   * {
   *  formattedPhoneNumber: (208) 123-4567,
   *  formattedNumber: format in RegExp,
   *  isValid: true
   * }
   *
   * @param str - i.e. 2081234567
   * @param countryCode - i.e. 'US' defaults to cc 1
   */
  format: function format(str, countryCode = 1) {
    if (!str) {
      return ''
    }
    try {
      var phoneStr = '+' + countryCode + ' ' + str
      var number = phoneUtil.parseAndKeepRawInput(phoneStr)
      var locale = phoneUtil.getRegionCodeForNumber(number)

      var isValid = phoneUtil.isPossibleNumber(
        phoneUtil.parse(phoneStr, locale)
      )

      // strip characters to digits only
      const digits = str.replace(/\D/g, '')
      const countryCodeISO = countryCodes[+countryCode] || 'US'
      const formatter = new AsYouTypeFormatter(countryCodeISO)

      digits.split('').forEach(digit => {
        formatter.inputDigit(digit)
      })

      var formattedNumber = formatter.currentOutput_
        .split('')
        .map(char => (!/\d/.test(char) ? char : /\d/))

      if (!formatter.ableToFormat_) {
        return false
      }

      return {
        formattedPhoneNumber: formatter.currentOutput_,
        formattedNumber,
        isValid,
      }
    } catch (error) {
      console.error(error)
      console.error(
        'i18nUtils.phone could not parse the input: ' +
          str +
          ', phoneUtils error for unsupported format or non-parsable phone number.'
      )
      return str
    }
  },

  isValidCountryCode: countryCode => !!countryCodes[countryCode],
}

module.exports = { phone, utils }
